/**
 * useSubscriptionStatus Hook
 * Shared between web and mobile for subscription status and billing portal
 */

import { useCallback, useEffect, useState } from 'react';
import { ApiError, type HttpClient } from '../api/httpClient';

// =============================================================================
// Types
// =============================================================================

export interface SubscriptionInfo {
  status: string;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
}

export interface UseSubscriptionStatusOptions {
  httpClient: HttpClient;
  autoLoad?: boolean;
}

export interface UseSubscriptionStatusResult {
  subscription: SubscriptionInfo | null;
  loading: boolean;
  error: string | null;
  /** Returns the billing portal URL. Consumer handles navigation. */
  getBillingPortalUrl: () => Promise<string | null>;
  portalLoading: boolean;
  refetch: () => Promise<void>;
}

// =============================================================================
// Hook
// =============================================================================

export function useSubscriptionStatus(
  options: UseSubscriptionStatusOptions,
): UseSubscriptionStatusResult {
  const { httpClient, autoLoad = true } = options;
  const [subscription, setSubscription] = useState<SubscriptionInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [portalLoading, setPortalLoading] = useState(false);

  const fetchStatus = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      const data = await httpClient.get<{ subscription?: SubscriptionInfo }>(
        '/api/subscription/status',
      );
      setSubscription(data.subscription ?? null);
    } catch (err) {
      setError(ApiError.getUserMessage(err, 'Failed to load subscription status'));
    } finally {
      setLoading(false);
    }
  }, [httpClient]);

  const getBillingPortalUrl = useCallback(async (): Promise<string | null> => {
    setPortalLoading(true);
    setError(null);

    try {
      const data = await httpClient.post<{ url: string }>('/api/subscription/portal');
      return data.url;
    } catch (err) {
      setError(ApiError.getUserMessage(err, 'Failed to open billing portal'));
      return null;
    } finally {
      setPortalLoading(false);
    }
  }, [httpClient]);

  useEffect(() => {
    if (autoLoad) {
      fetchStatus();
    }
  }, [autoLoad, fetchStatus]);

  return {
    subscription,
    loading,
    error,
    getBillingPortalUrl,
    portalLoading,
    refetch: fetchStatus,
  };
}
